
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";

const API_URL = `${process.env.REACT_APP_API_URL}/sim/sim-numbers`;

const initialFilters = {
  simNumber: "",
  saralId: "",
  mobileNumber: "",
  registeredCompany: "",
  isActive: "",
  inUse: "",
  installationDate: "",
  expiryDate: "",
  isWarrantyExpired: "",
};

const inputClass =
  "w-full rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-yellow-500 focus:ring-4 focus:ring-yellow-100";

function Icon({ name, className = "h-5 w-5" }) {
  const common = {
    className,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };

  const paths = {
    search: <><circle cx="11" cy="11" r="7" /><path d="m20 20-4-4" /></>,
    farmer: <><circle cx="12" cy="8" r="3.5" /><path d="M5 21v-2a7 7 0 0 1 14 0v2" /><path d="M8.5 5.5 7 3h10l-1.5 2.5" /><path d="M9 14h6" /></>,
    filter: <path d="M4 5h16l-6.5 7.5V19l-3 1v-7.5L4 5Z" />,
    sim: <><rect x="5" y="2.5" width="14" height="19" rx="2" /><path d="M9 7h6v4H9zM9 14h2v2H9zM13 14h2v2h-2z" /></>,
    phone: <path d="M7 3h3l2 5-2 1.5a15 15 0 0 0 4.5 4.5L16 12l5 2v3a3 3 0 0 1-3 3A16 16 0 0 1 4 6a3 3 0 0 1 3-3Z" />,
    building: <><rect x="4" y="3" width="16" height="18" rx="1" /><path d="M8 7h2m4 0h2M8 11h2m4 0h2M8 15h2m4 0h2M10 21v-3h4v3" /></>,
    eye: <><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" /><circle cx="12" cy="12" r="3" /></>,
    refresh: <><path d="M20 7v5h-5M4 17v-5h5" /><path d="M5.5 9A7 7 0 0 1 18 6l2 6M4 12l2 6a7 7 0 0 0 12.5-3" /></>,
    first: <><path d="m11 17-5-5 5-5M18 17l-5-5 5-5" /><path d="M3 5v14" /></>,
    last: <><path d="m13 17 5-5-5-5M6 17l5-5-5-5" /><path d="M21 5v14" /></>,
    chevronLeft: <path d="m15 18-6-6 6-6" />,
    chevronRight: <path d="m9 18 6-6-6-6" />,
    chevronDown: <path d="m6 9 6 6 6-6" />,
    close: <path d="m18 6-12 12M6 6l12 12" />,
    list: <><path d="M9 6h11M9 12h11M9 18h11" /><path d="M4 6h.01M4 12h.01M4 18h.01" /></>,
    calendar: <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M16 3v4M8 3v4M3 10h18" /></>,
    clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
    shield: <><path d="M12 22s8-4 8-11V5l-8-3-8 3v6c0 7 8 11 8 11Z" /><path d="m9 12 2 2 4-4" /></>,
  };

  return <svg {...common}>{paths[name] || paths.search}</svg>;
}

function StatusBadge({ active }) {
  return (
    <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold ${active
        ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100"
        : "bg-red-50 text-red-600 ring-1 ring-red-100"
      }`}>
      <span className={`h-1.5 w-1.5 rounded-full ${active ? "bg-emerald-500" : "bg-red-500"}`} />
      {active ? "Active" : "Blocked"}
    </span>
  );
}

function BooleanBadge({ value, trueLabel = "Yes", falseLabel = "No" }) {
  if (value === null || value === undefined) {
    return <span className="text-gray-400">—</span>;
  }

  return (
    <span className={`inline-flex whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold ${value
        ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100"
        : "bg-gray-100 text-gray-600 ring-1 ring-gray-200"
      }`}>
      {value ? trueLabel : falseLabel}
    </span>
  );
}

function WarrantyBadge({ value }) {
  if (value === null || value === undefined) {
    return <span className="text-gray-400">—</span>;
  }

  return (
    <span className={`inline-flex whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold ${value
        ? "bg-red-50 text-red-600 ring-1 ring-red-100"
        : "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100"
      }`}>
      {value ? "Expired" : "Valid"}
    </span>
  );
}

function FilterField({ label, icon, children }) {
  return (
    <div className="min-w-0">
      <label className="mb-2 block text-sm font-semibold text-gray-700">{label}</label>
      <div className="relative">
        {icon && (
          <Icon
            name={icon}
            className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
          />
        )}
        {children}
      </div>
    </div>
  );
}

function DetailCard({ label, value, icon }) {
  return (
    <div className="rounded-xl border border-gray-100 bg-white p-4 transition hover:border-yellow-200 hover:shadow-sm">
      <div className="mb-2 flex items-center gap-2 text-gray-400">
        {icon && <Icon name={icon} className="h-4 w-4" />}
        <span className="text-xs font-semibold uppercase tracking-wide">{label}</span>
      </div>
      <div className="break-all text-sm font-bold text-gray-900">
        {value === null || value === undefined || value === "" ? "—" : String(value)}
      </div>
    </div>
  );
}

function normalizeFilters(values) {
  return Object.fromEntries(
    Object.entries(values).map(([key, value]) => [
      key,
      typeof value === "string" ? value.trim() : value,
    ])
  );
}

function isValidDateFormat(value) {
  if (!value) return true;

  const match = value.match(/^(0[1-9]|[12]\d|3[01])\.(0[1-9]|1[0-2])\.(\d{4})$/);
  if (!match) return false;

  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));

  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

function formatDateTime(value) {
  if (!value) return "—";

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

export default function SimDetails() {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [records, setRecords] = useState([]);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalRecords: 0,
    totalPages: 0,
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [filterError, setFilterError] = useState("");

  const normalizedFilters = useMemo(() => normalizeFilters(filters), [filters]);
  const normalizedAppliedFilters = useMemo(
    () => normalizeFilters(appliedFilters),
    [appliedFilters]
  );

  const filtersAreEqual = useMemo(
    () => Object.keys(initialFilters).every(
      key => normalizedFilters[key] === normalizedAppliedFilters[key]
    ),
    [normalizedFilters, normalizedAppliedFilters]
  );

  const hasDraftFilters = Object.values(normalizedFilters).some(Boolean);
  const hasAppliedFilters = Object.values(normalizedAppliedFilters).some(Boolean);
  const activeFilterCount = Object.values(normalizedAppliedFilters).filter(Boolean).length;

  const fetchRecords = useCallback(async (page, limit, activeFilters, signal) => {
    setLoading(true);
    setError("");

    const params = { page, limit };

    Object.entries(normalizeFilters(activeFilters)).forEach(([key, value]) => {
      if (value !== "") params[key] = value;
    });

    try {
      const response = await axios.get(API_URL, {
        params,
        signal,
        headers: { Accept: "application/json" },
      });

      const result = response.data;

      if (result.success === false) {
        throw new Error(result.message || "Unable to load SIM records.");
      }

      setRecords(Array.isArray(result.data) ? result.data : []);

      setPagination(previous => ({
        ...previous,
        ...(result.pagination || {
          page,
          limit,
          totalRecords: result.data?.length || 0,
          totalPages: 1,
        }),
      }));
    } catch (err) {
      if (err.code === "ERR_CANCELED" || axios.isCancel(err)) return;

      setError(
        err.response?.data?.message ||
        err.message ||
        "Unable to load SIM records."
      );
      setRecords([]);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    fetchRecords(
      pagination.page,
      pagination.limit,
      appliedFilters,
      controller.signal
    );

    return () => controller.abort();
  }, [
    pagination.page,
    pagination.limit,
    appliedFilters,
    refreshKey,
    fetchRecords,
  ]);

  const updateFilter = (key, value) => {
    setFilters(previous => ({ ...previous, [key]: value }));
    setFilterError("");
  };

  const applyFilters = event => {
    event.preventDefault();

    if (
      !isValidDateFormat(normalizedFilters.installationDate) ||
      !isValidDateFormat(normalizedFilters.expiryDate)
    ) {
      setFilterError("Please enter valid SIM dates in DD.MM.YYYY format.");
      return;
    }

    if (filtersAreEqual) {
      setFiltersOpen(false);
      return;
    }

    setPagination(previous => ({ ...previous, page: 1 }));
    setAppliedFilters({ ...normalizedFilters });
    setFiltersOpen(false);
    setFilterError("");
  };

  const clearFilters = () => {
    if (!hasDraftFilters && !hasAppliedFilters) return;

    setFilters({ ...initialFilters });
    setAppliedFilters({ ...initialFilters });
    setPagination(previous => ({ ...previous, page: 1 }));
    setFilterError("");
  };

  const changePage = page => {
    if (
      loading ||
      page < 1 ||
      page > pagination.totalPages ||
      page === pagination.page
    ) return;

    setPagination(previous => ({ ...previous, page }));
  };

  const changeLimit = event => {
    setPagination(previous => ({
      ...previous,
      limit: Number(event.target.value),
      page: 1,
    }));
  };

  const refreshRecords = () => setRefreshKey(previous => previous + 1);

  const startRecord = pagination.totalRecords === 0
    ? 0
    : (pagination.page - 1) * pagination.limit + 1;

  const endRecord = Math.min(
    pagination.page * pagination.limit,
    pagination.totalRecords
  );

  const getPageNumbers = () => {
    const total = pagination.totalPages;
    const current = pagination.page;

    if (total <= 5) {
      return Array.from({ length: total }, (_, index) => index + 1);
    }

    const start = Math.max(1, Math.min(current - 2, total - 4));
    return Array.from({ length: 5 }, (_, index) => start + index);
  };

  const pageButtonClass =
    "flex h-9 min-w-9 items-center justify-center rounded-lg border border-gray-200 bg-white px-2 text-sm font-semibold text-gray-600 transition hover:bg-yellow-100 disabled:cursor-not-allowed disabled:opacity-40";

  return (
    <section className="min-h-full bg-gray-50/80 px-4 py-5 sm:px-6 lg:px-8 lg:py-8">
      <div className="mx-auto max-w-[1600px] space-y-5">

        {/* Page Header */}
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-yellow-300 text-gray-950 shadow-sm shadow-yellow-200">
              <Icon name="sim" className="h-7 w-7" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-gray-950 sm:text-3xl">
                SIM Details
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                View, search and manage your SIM records.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={refreshRecords}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 self-start rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm transition hover:border-yellow-400 hover:bg-yellow-50 disabled:opacity-50 sm:self-auto"
          >
            <Icon name="refresh" className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>

        {/* Collapsible Filters */}
        <div className="overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-sm">
          <button
            type="button"
            onClick={() => setFiltersOpen(open => !open)}
            aria-expanded={filtersOpen}
            aria-controls="sim-filters-panel"
            className="flex w-full items-center justify-between gap-4 px-4 py-4 text-left transition hover:bg-yellow-50/50 sm:px-6"
          >
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-yellow-100 text-gray-900">
                <Icon name="filter" className="h-5 w-5" />
              </div>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="font-bold text-gray-900">Filters</h2>
                  {activeFilterCount > 0 && (
                    <span className="rounded-full bg-yellow-300 px-2 py-0.5 text-xs font-bold text-gray-900">
                      {activeFilterCount} applied
                    </span>
                  )}
                </div>
                <p className="mt-0.5 text-xs text-gray-500">
                  {filtersOpen
                    ? "Hide filters to see more records"
                    : "Filter by SIM, Saral ID, company, status and dates"}
                </p>
              </div>
            </div>

            <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 transition-transform duration-200 ${filtersOpen ? "rotate-180" : ""}`}>
              <Icon name="chevronDown" className="h-4 w-4" />
            </span>
          </button>

          {filtersOpen && (
            <form
              id="sim-filters-panel"
              onSubmit={applyFilters}
              className="border-t border-gray-200 px-4 py-5 sm:px-5"
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">

                <FilterField label="SIM Number" icon="sim">
                  <input
                    value={filters.simNumber}
                    onChange={e => updateFilter("simNumber", e.target.value)}
                    placeholder="Search SIM number..."
                    className={`${inputClass} pl-10`}
                  />
                </FilterField>

                <FilterField label="Saral ID" icon="farmer">
                  <input
                    value={filters.saralId}
                    onChange={e => updateFilter("saralId", e.target.value)}
                    placeholder="Enter Saral ID"
                    className={`${inputClass} pl-10`}
                  />
                </FilterField>

                <FilterField label="Mobile Number" icon="phone">
                  <input
                    value={filters.mobileNumber}
                    onChange={e => updateFilter("mobileNumber", e.target.value)}
                    placeholder="Search mobile number..."
                    className={`${inputClass} pl-10`}
                  />
                </FilterField>

                <FilterField label="Registered Company" icon="building">
                  <select
                    value={filters.registeredCompany}
                    onChange={e => updateFilter("registeredCompany", e.target.value)}
                    className={`${inputClass} pl-10`}
                  >
                    <option value="">All companies</option>
                    <option value="GSPL">GSPL</option>
                    <option value="GEPL">GEPL</option>
                  </select>
                </FilterField>

                <FilterField label="SIM Status">
                  <select
                    value={filters.isActive}
                    onChange={e => updateFilter("isActive", e.target.value)}
                    className={inputClass}
                  >
                    <option value="">All statuses</option>
                    <option value="true">Active</option>
                    <option value="false">Blocked</option>
                  </select>
                </FilterField>

                <FilterField label="SIM Usage">
                  <select
                    value={filters.inUse}
                    onChange={e => updateFilter("inUse", e.target.value)}
                    className={inputClass}
                  >
                    <option value="">All SIMs</option>
                    <option value="true">In Use / Assigned</option>
                    <option value="false">Not In Use</option>
                  </select>
                </FilterField>

                <FilterField label="Warranty Status">
                  <select
                    value={filters.isWarrantyExpired}
                    onChange={e => updateFilter("isWarrantyExpired", e.target.value)}
                    className={inputClass}
                  >
                    <option value="">All warranty statuses</option>
                    <option value="false">Warranty Valid</option>
                    <option value="true">Warranty Expired</option>
                  </select>
                </FilterField>

                <FilterField label="Installation Date" icon="calendar">
                  <input
                    type="text"
                    value={filters.installationDate}
                    onChange={e => updateFilter("installationDate", e.target.value)}
                    placeholder="DD.MM.YYYY"
                    maxLength={10}
                    className={`${inputClass} pl-10`}
                  />
                </FilterField>

                <FilterField label="Expiry Date" icon="calendar">
                  <input
                    type="text"
                    value={filters.expiryDate}
                    onChange={e => updateFilter("expiryDate", e.target.value)}
                    placeholder="DD.MM.YYYY"
                    maxLength={10}
                    className={`${inputClass} pl-10`}
                  />
                </FilterField>
              </div>

              {filterError && (
                <p className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">
                  {filterError}
                </p>
              )}

              <div className="mt-5 flex flex-col-reverse gap-3 border-t border-gray-100 pt-5 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-gray-400">
                  Combine filters to find specific records. SIM dates must use DD.MM.YYYY format.
                </p>

                <div className="flex flex-col gap-2 sm:flex-row">
                  <button
                    type="button"
                    onClick={clearFilters}
                    disabled={!hasDraftFilters && !hasAppliedFilters}
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <Icon name="close" className="h-4 w-4" />
                    Clear filters
                  </button>

                  <button
                    type="submit"
                    disabled={filtersAreEqual}
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-yellow-400 px-5 py-2.5 text-sm font-bold text-gray-950 shadow-sm transition hover:bg-yellow-500 focus:outline-none focus:ring-4 focus:ring-yellow-200 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <Icon name="search" className="h-4 w-4" />
                    Apply filters
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>

        {/* SIM Records - Original table columns preserved */}
        <div className="overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-sm">
          <div className="flex flex-col gap-4 border-b border-gray-100 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-yellow-300 text-gray-950">
                <Icon name="list" className="h-5 w-5" />
              </div>
              <div>
                <h2 className="font-bold text-gray-900">SIM Records</h2>
                <p className="text-xs text-gray-500">
                  {pagination.totalRecords.toLocaleString()} records found
                </p>
              </div>
            </div>

            <label className="flex items-center gap-2 self-start text-sm text-gray-500 sm:self-auto">
              Show
              <select
                value={pagination.limit}
                onChange={changeLimit}
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 font-medium text-gray-800 outline-none focus:border-yellow-500 focus:ring-2 focus:ring-yellow-100"
              >
                {[10, 20, 50, 100].map(number => (
                  <option key={number} value={number}>{number}</option>
                ))}
              </select>
              per page
            </label>
          </div>

          {error && (
            <div role="alert" className="m-4 flex flex-col gap-3 rounded-xl border border-red-100 bg-red-50 p-4 text-sm text-red-700 sm:flex-row sm:items-center sm:justify-between">
              <span>{error}</span>
              <button type="button" onClick={refreshRecords} className="self-start font-bold underline sm:self-auto">
                Try again
              </button>
            </div>
          )}

          <div className="w-full overflow-x-auto">
            <table className="w-full min-w-[1450px] border-collapse text-left text-sm">
              <thead>
                <tr className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <th className="whitespace-nowrap px-5 py-4 font-semibold">S.No.</th>
                  <th className="whitespace-nowrap px-5 py-4 font-semibold">SIM Number</th>
                  <th className="whitespace-nowrap px-5 py-4 font-semibold">SARAL ID</th>
                  <th className="whitespace-nowrap px-5 py-4 font-semibold">Mobile Number</th>
                  <th className="whitespace-nowrap px-5 py-4 font-semibold">SIM Status</th>
                  <th className="whitespace-nowrap px-5 py-4 font-semibold">Usage</th>
                  <th className="whitespace-nowrap px-5 py-4 font-semibold">Company</th>
                  <th className="whitespace-nowrap px-5 py-4 font-semibold">Installation Date</th>
                  <th className="whitespace-nowrap px-5 py-4 font-semibold">Expiry Date</th>
                  <th className="whitespace-nowrap px-5 py-4 font-semibold">Warranty</th>
                  <th className="whitespace-nowrap px-5 py-4 text-right font-semibold">Action</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <tr key={index} className="animate-pulse">
                      {Array.from({ length: 11 }).map((__, cell) => (
                        <td key={cell} className="px-5 py-5">
                          <div className="h-4 rounded bg-gray-100" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : records.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="px-5 py-16 text-center">
                      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-yellow-100 text-gray-700">
                        <Icon name="search" className="h-6 w-6" />
                      </div>
                      <p className="mt-3 font-semibold text-gray-800">No SIM records found</p>
                      <p className="mt-1 text-sm text-gray-500">
                        Try changing your filters or clear them to see all records.
                      </p>
                      <button
                        type="button"
                        onClick={clearFilters}
                        disabled={!hasDraftFilters && !hasAppliedFilters}
                        className="mt-4 rounded-lg bg-yellow-300 px-4 py-2 text-sm font-bold text-gray-900 hover:bg-yellow-400 disabled:opacity-40"
                      >
                        Clear filters
                      </button>
                    </td>
                  </tr>
                ) : (
                  records.map((record, index) => (
                    <tr
                      key={record.id || record.simNumber || index}
                      className="transition hover:bg-yellow-50/40"
                    >
                      <td className="whitespace-nowrap px-5 py-4 font-medium text-gray-500">
                        {(pagination.page - 1) * pagination.limit + index + 1}
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 font-semibold text-gray-900">
                        {record.simNumber || "—"}
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 text-gray-900">
                        {record.saralId || <span className="text-gray-400">Not Assigned</span>}
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 text-gray-700">
                        {record.mobileNumber || "—"}
                      </td>
                      <td className="whitespace-nowrap px-5 py-4">
                        <StatusBadge active={record.isActive === true} />
                      </td>
                      <td className="whitespace-nowrap px-5 py-4">
                        <BooleanBadge value={record.inUse} trueLabel="In Use" falseLabel="Not In Use" />
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 text-gray-700">
                        {record.registeredCompany || "—"}
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 text-gray-700">
                        {record.installationDate || "—"}
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 text-gray-700">
                        {record.expiryDate || "—"}
                      </td>
                      <td className="whitespace-nowrap px-5 py-4">
                        <WarrantyBadge value={record.isWarrantyExpired} />
                      </td>
                      <td className="whitespace-nowrap px-5 py-4 text-right">
                        <button
                          type="button"
                          onClick={() => setSelectedRecord(record)}
                          className="inline-flex items-center gap-2 rounded-lg bg-yellow-100 px-3.5 py-2 text-xs font-bold text-gray-900 transition hover:bg-yellow-300 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                        >
                          <Icon name="eye" className="h-4 w-4" />
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex flex-col gap-4 border-t border-gray-100 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
            <p className="text-sm text-gray-500">
              Showing <span className="font-semibold text-gray-800">{startRecord}–{endRecord}</span>
              {" "}of{" "}
              <span className="font-semibold text-gray-800">{pagination.totalRecords.toLocaleString()}</span>
              {" "}records
            </p>

            <div className="flex flex-wrap items-center gap-1.5">
              <button
                type="button"
                disabled={loading || pagination.page <= 1}
                onClick={() => changePage(1)}
                aria-label="First page"
                title="First page"
                className={pageButtonClass}
              >
                <Icon name="first" className="h-4 w-4" />
              </button>

              <button
                type="button"
                disabled={loading || pagination.page <= 1}
                onClick={() => changePage(pagination.page - 1)}
                aria-label="Previous page"
                title="Previous page"
                className={pageButtonClass}
              >
                <Icon name="chevronLeft" className="h-4 w-4" />
              </button>

              {getPageNumbers().map(page => (
                <button
                  key={page}
                  type="button"
                  disabled={loading}
                  onClick={() => changePage(page)}
                  aria-current={pagination.page === page ? "page" : undefined}
                  className={`h-9 min-w-9 rounded-lg px-2.5 text-sm font-semibold transition ${pagination.page === page
                      ? "border border-yellow-400 bg-yellow-400 text-gray-950 shadow-sm"
                      : "border border-gray-200 bg-white text-gray-600 hover:bg-yellow-50"
                    }`}
                >
                  {page}
                </button>
              ))}

              {pagination.totalPages > 5 && pagination.page < pagination.totalPages - 2 && (
                <>
                  <span className="px-1 text-gray-400">…</span>
                  <button
                    type="button"
                    disabled={loading}
                    onClick={() => changePage(pagination.totalPages)}
                    className={pageButtonClass}
                  >
                    {pagination.totalPages}
                  </button>
                </>
              )}

              <button
                type="button"
                disabled={loading || pagination.page >= pagination.totalPages}
                onClick={() => changePage(pagination.page + 1)}
                aria-label="Next page"
                title="Next page"
                className={pageButtonClass}
              >
                <Icon name="chevronRight" className="h-4 w-4" />
              </button>

              <button
                type="button"
                disabled={loading || pagination.page >= pagination.totalPages || pagination.totalPages === 0}
                onClick={() => changePage(pagination.totalPages)}
                aria-label="Last page"
                title="Last page"
                className={pageButtonClass}
              >
                <Icon name="last" className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Redesigned SIM Details Modal */}
      {/* Professional record details modal */}
      {selectedRecord && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-gray-950/50 p-4"
          onMouseDown={event => {
            if (event.target === event.currentTarget) {
              setSelectedRecord(null);
            }
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="sim-details-modal-title"
            className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-5">
              <div>
                <h2
                  id="sim-details-modal-title"
                  className="text-lg font-semibold tracking-tight text-gray-900"
                >
                  SIM Record Details
                </h2>
                <p className="mt-1 text-sm text-gray-500">
                  Detailed information about this SIM record
                </p>
              </div>

              <button
                type="button"
                onClick={() => setSelectedRecord(null)}
                aria-label="Close details"
                className="rounded-lg p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-300"
              >
                <Icon name="close" className="h-5 w-5" />
              </button>
            </div>

            {/* Scrollable content */}
            <div
              className="min-h-0 flex-1 overflow-y-auto px-6 py-5 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
            >
              {/* SIM Number highlight */}
              <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 px-4 py-4">
                <p className="text-xs font-medium uppercase tracking-wider text-gray-500">
                  SIM Number
                </p>
                <p className="mt-2 break-all text-lg font-semibold tracking-wide text-gray-900">
                  {selectedRecord.simNumber || "—"}
                </p>
              </div>

              {/* General information */}
              <div>
                <h3 className="mb-3 text-sm font-semibold text-gray-900">
                  General Information
                </h3>

                <div className="overflow-hidden rounded-lg border border-gray-200">
                  {[
                    ["Saral ID", selectedRecord.saralId],
                    ["Mobile Number", selectedRecord.mobileNumber],
                    ["Registered Company", selectedRecord.registeredCompany],
                  ].map(([label, value], index) => (
                    <div
                      key={label}
                      className={`grid grid-cols-1 gap-1 px-4 py-3 sm:grid-cols-2 sm:items-center ${index !== 2 ? "border-b border-gray-100" : ""
                        }`}
                    >
                      <span className="text-sm text-gray-500">
                        {label}
                      </span>
                      <span className="break-all text-sm font-medium text-gray-900 sm:text-right">
                        {value || "—"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Status information */}
              <div className="mt-6">
                <h3 className="mb-3 text-sm font-semibold text-gray-900">
                  Status Information
                </h3>

                <div className="overflow-hidden rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between gap-4 border-b border-gray-100 px-4 py-3">
                    <span className="text-sm text-gray-500">
                      SIM Status
                    </span>
                    <StatusBadge active={selectedRecord.isActive} />
                  </div>

                  <div className="flex items-center justify-between gap-4 px-4 py-3">
                    <span className="text-sm text-gray-500">
                      Usage Status
                    </span>
                    <span
                      className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-semibold ${selectedRecord.inUse
                          ? "bg-blue-50 text-blue-700"
                          : "bg-gray-100 text-gray-600"
                        }`}
                    >
                      {selectedRecord.inUse ? "In Use" : "Available"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Installation and warranty information */}
              <div className="mt-6">
                <h3 className="mb-3 text-sm font-semibold text-gray-900">
                  Installation & Warranty
                </h3>

                <div className="overflow-hidden rounded-lg border border-gray-200">
                  {[
                    ["Installation Date", selectedRecord.installationDate],
                    ["Expiry Date", selectedRecord.expiryDate],
                    [
                      "Warranty Status",
                      selectedRecord.isWarrantyExpired === null ||
                        selectedRecord.isWarrantyExpired === undefined
                        ? "Not Available"
                        : selectedRecord.isWarrantyExpired
                          ? "Expired"
                          : "Valid",
                    ],
                  ].map(([label, value], index) => (
                    <div
                      key={label}
                      className={`grid grid-cols-1 gap-1 px-4 py-3 sm:grid-cols-2 sm:items-center ${index !== 3 ? "border-b border-gray-100" : ""
                        }`}
                    >
                      <span className="text-sm text-gray-500">
                        {label}
                      </span>

                      <span
                        className={`break-all text-sm font-medium sm:text-right ${label === "Warranty Status" &&
                            value === "Expired"
                            ? "text-red-600"
                            : label === "Warranty Status" &&
                              value === "Valid"
                              ? "text-emerald-700"
                              : "text-gray-900"
                          }`}
                      >
                        {value || "—"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Transfer history */}
              {Array.isArray(selectedRecord.transferredFrom) &&
                selectedRecord.transferredFrom.length > 0 && (
                  <div className="mt-6">
                    <h3 className="mb-3 text-sm font-semibold text-gray-900">
                      Transfer History
                    </h3>

                    <div className="rounded-lg border border-gray-200">
                      {selectedRecord.transferredFrom.map(
                        (simNumber, index) => (
                          <div
                            key={`${simNumber}-${index}`}
                            className={`flex items-center justify-between gap-3 px-4 py-3 ${index !==
                                selectedRecord.transferredFrom.length - 1
                                ? "border-b border-gray-100"
                                : ""
                              }`}
                          >
                            <span className="text-sm text-gray-500">
                              Previous SIM {index + 1}
                            </span>
                            <span className="break-all text-sm font-medium text-gray-900">
                              {simNumber}
                            </span>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}
            </div>

            {/* Footer */}
            <div className="flex justify-end border-t border-gray-200 bg-white px-6 py-4">
              <button
                type="button"
                onClick={() => setSelectedRecord(null)}
                className="rounded-lg border border-gray-300 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
